<template>
  <!-- App Bar -->
  <v-app-bar elevation="1" color="white" data-testid="app-header">
    <!-- Burger menu only in browser mode -->
    <v-app-bar-nav-icon
      v-if="showBurger"
      data-testid="drawer-toggle"
      @click="toggleDrawer"
    />

    <img
      src="/favicon.svg"
      alt="Candy Cleaning Services"
      class="logo-img"
      :style="
        showBurger
          ? 'width: 32px; height: 32px; margin-right: 12px'
          : 'width: 32px; height: 32px; margin-left: 12px; margin-right: 12px'
      "
    />

    <v-app-bar-title>{{ getPageTitle() }}</v-app-bar-title>

    <v-spacer />

    <span
      v-if="user"
      class="text-caption text-grey-darken-1 mr-3 d-none d-sm-flex"
    >
      Welcome{{ showBurger ? ' back' : '' }},
      <!-- Note: user from authState (REST API) uses snake_case -->
      {{ user.first_name || user.email }}!
    </span>
  </v-app-bar>

  <!-- Navigation Drawer (only in browser mode) -->
  <v-navigation-drawer
    v-if="showBurger"
    v-model="drawerOpen"
    temporary
    width="320"
    data-testid="app-drawer"
  >
    <!-- Drawer Header -->
    <v-sheet color="primary" class="pa-4">
      <div class="d-flex align-center">
        <v-avatar v-if="user" color="white" size="60" class="mr-3">
          <span class="text-primary text-h6 font-weight-bold">
            {{ getAvatarInitials() }}
          </span>
        </v-avatar>

        <div v-if="user" style="flex: 1; min-width: 0">
          <!-- Note: user from authState (REST API) uses snake_case -->
          <div class="text-subtitle-1 font-weight-bold white--text">
            {{ user.first_name || user.email }}
          </div>
          <div class="text-caption white--text text-opacity-90">
            {{ getRoleDisplayName() }}
          </div>
          <div class="text-caption white--text text-opacity-80">
            {{ user.email }}
          </div>
        </div>

        <div v-else>
          <div class="text-subtitle-1 font-weight-bold white--text">
            Welcome to Candy Cleaning
          </div>
          <div class="text-caption white--text text-opacity-90">
            Please sign in to continue
          </div>
        </div>
      </div>
    </v-sheet>

    <v-divider />

    <!-- Authenticated User Navigation -->
    <v-list v-if="user" nav>
      <v-list-subheader>Navigation</v-list-subheader>
      <v-list-item
        v-for="item in navItems"
        :key="item.id"
        :data-testid="`nav-${item.id}`"
        color="primary"
        @click="handleNavigation(item.path)"
      >
        <template #prepend>
          <span style="font-size: 1.25rem; width: 24px; text-align: center">
            {{ item.icon }}
          </span>
        </template>
        <v-list-item-title>{{ item.label }}</v-list-item-title>
        <template v-if="item.badge" #append>
          <v-badge color="error" :content="item.badge" inline />
        </template>
      </v-list-item>
    </v-list>

    <!-- Guest Navigation -->
    <v-list v-if="!user" nav>
      <v-list-subheader>Get Started</v-list-subheader>
      <v-list-item
        color="primary"
        data-testid="nav-login"
        @click="handleNavigation('/login')"
      >
        <template #prepend>
          <span style="font-size: 1.25rem; width: 24px; text-align: center"
            >🔑</span
          >
        </template>
        <v-list-item-title class="font-weight-medium">
          Login
        </v-list-item-title>
      </v-list-item>

      <v-list-item
        color="primary"
        data-testid="nav-register"
        @click="handleNavigation('/register')"
      >
        <template #prepend>
          <span style="font-size: 1.25rem; width: 24px; text-align: center"
            >📝</span
          >
        </template>
        <v-list-item-title class="font-weight-medium">
          Sign Up
        </v-list-item-title>
      </v-list-item>
    </v-list>
  </v-navigation-drawer>
</template>

<script setup lang="ts">
import { ref, toRef, computed } from 'vue'
import { useRoute } from 'vue-router'
import { useNavigation } from '../composables/useNavigation'
import { useRouterNavigation } from '../composables/useRouterNavigation'
import { useAuthState } from '../composables/useAuthState'
import type { User } from '@/api'

interface Props {
  user: User | null
  showBurger?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  showBurger: true,
})
const route = useRoute()
const authState = useAuthState()
const { navigateTo } = useRouterNavigation()

// Convert props.user to a ref for useNavigation composable
const userRef = toRef(props, 'user')
const { getNavItems } = useNavigation(userRef)

// Get navigation items from composable
const navItems = computed(() => getNavItems.value)

const drawerOpen = ref(false)

const toggleDrawer = () => {
  drawerOpen.value = !drawerOpen.value
}

const closeDrawer = () => {
  drawerOpen.value = false
}

/**
 * Handle navigation with proper drawer closing sequence
 * Prevents race condition between navigation and drawer closing
 */
const handleNavigation = async (path: string) => {
  // Navigate first (composable handles duplicate route checks)
  await navigateTo(path)

  // Close drawer after navigation attempt (success or failure)
  closeDrawer()
}

const getPageTitle = (): string => {
  const metaTitle = route.meta.title as string | undefined
  if (metaTitle) {
    return metaTitle.replace(' - Candy Cleaning Services', '')
  }
  return 'Candy Cleaning Services'
}

const getAvatarInitials = (): string => {
  if (!props.user) return ''

  // Note: user from authState (REST API) uses snake_case
  const firstName = props.user.first_name || ''
  const lastName = props.user.last_name || ''

  if (firstName && lastName) {
    return (firstName[0] + lastName[0]).toUpperCase()
  } else if (firstName) {
    return firstName[0].toUpperCase()
  } else if (props.user.email) {
    return props.user.email[0].toUpperCase()
  }

  return '?'
}

const getRoleDisplayName = (): string => {
  if (authState.isCustomer.value) return 'Customer'
  if (authState.isSuperAdmin.value) return 'Super Administrator'
  if (authState.hasRole('admin-manager')) return 'Manager'
  if (authState.isServiceProvider.value) return 'Service Provider'
  return 'User'
}
</script>
